<template>
  <div class="ai-email-config">
    <div class="config-row config-row--primary">
      <div class="field-group">
        <select
          :value="actionName"
          class="action__question w-full"
          @change="onActionChange"
        >
          <option
            v-for="actionType in actionTypes"
            :key="actionType.key"
            :value="actionType.key"
          >
            {{ actionType.label }}
          </option>
        </select>
      </div>

      <div class="field-group">
        <div class="multiselect-wrap--small ai-email-multiselect">
          <multiselect
            v-model="selectedInbox"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.INBOX_PLACEHOLDER')"
            :options="inboxOptions"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            :max-height="180"
            @select="onInboxSelect"
          >
            <template #singleLabel="{ option }">
              <span v-tooltip="option.name" class="single-label-truncate">
                {{ option.name }}
              </span>
            </template>
            <template #option="{ option }">
              <span :title="option.name" class="option-truncate">
                {{ option.name }}
              </span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="field-group">
        <div class="multiselect-wrap--small ai-email-multiselect">
          <multiselect
            v-model="selectedMacro"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.MACRO_PLACEHOLDER')"
            :options="macroOptions"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            :max-height="180"
            @select="onMacroSelect"
          >
            <template #singleLabel="{ option }">
              <span v-tooltip="option.name" class="single-label-truncate">
                {{ option.name }}
              </span>
            </template>
            <template #option="{ option }">
              <span :title="option.name" class="option-truncate">
                {{ option.name }}
              </span>
            </template>
          </multiselect>
        </div>
      </div>

      <woot-button
        v-if="showRemove"
        icon="dismiss"
        variant="clear"
        color-scheme="secondary"
        class="remove-action-btn"
        @click="$emit('remove-action')"
      />
    </div>

    <div class="config-row config-row--attachment">
      <div class="field-group field-group--full">
        <automation-action-file-input
          :value="attachmentBlobIds"
          :initial-attachments="initialAttachments"
          variant="bar"
          multiple
          :allow-remove="true"
          @input="onAttachmentInput"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import AutomationActionFileInput from './AutomationFileInput.vue';

export default {
  components: { Multiselect, AutomationActionFileInput },
  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
    initialAttachments: {
      type: Array,
      default: () => [],
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    actionName: {
      type: String,
      default: '',
    },
    showRemove: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localConfig: {
        inbox_id: null,
        macro_id: null,
        attachment_blob_ids: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      emailInboxes: 'inboxes/getEmailInboxes',
      allMacros: 'macros/getMacros',
    }),
    inboxOptions() {
      return (this.emailInboxes || []).map(inbox => ({
        id: inbox.id,
        name: inbox.name,
      }));
    },
    macroOptions() {
      return (this.allMacros || [])
        .filter(macro => this.macroHasAutoExecuteApiCall(macro))
        .map(macro => ({ id: macro.id, name: macro.name }));
    },
    selectedInbox: {
      get() {
        return (
          this.inboxOptions.find(
            i => String(i.id) === String(this.localConfig.inbox_id)
          ) || null
        );
      },
      set() {
        // handled by @select
      },
    },
    selectedMacro: {
      get() {
        return (
          this.macroOptions.find(
            m => String(m.id) === String(this.localConfig.macro_id)
          ) || null
        );
      },
      set() {
        // handled by @select
      },
    },
    attachmentBlobIds() {
      return Array.isArray(this.localConfig.attachment_blob_ids)
        ? this.localConfig.attachment_blob_ids.filter(Boolean)
        : [];
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.hydrateFromValue(newValue);
      },
      deep: true,
      immediate: true,
    },
    inboxOptions() {
      this.hydrateFromValue(this.value);
    },
    macroOptions() {
      this.hydrateFromValue(this.value);
    },
  },
  mounted() {
    this.emitUpdate();
  },
  methods: {
    onActionChange(event) {
      this.$emit('update:action-name', event.target.value);
    },
    macroHasAutoExecuteApiCall(macro) {
      if (!macro || !Array.isArray(macro.actions)) return false;
      return macro.actions.some(action => {
        if (action.action_name !== 'send_api_webhook') return false;
        const params = Array.isArray(action.action_params)
          ? action.action_params[0]
          : action.action_params;
        return params && params.auto_execute === true;
      });
    },
    hydrateFromValue(value) {
      const source = Array.isArray(value) ? value[0] : value;
      if (source && typeof source === 'object') {
        this.localConfig = {
          inbox_id: source.inbox_id ?? null,
          macro_id: source.macro_id ?? null,
          attachment_blob_ids: this.normalizeAttachmentBlobIds(source),
        };
      }
    },
    onInboxSelect(option) {
      this.localConfig.inbox_id = option?.id ?? null;
      this.emitUpdate();
    },
    onMacroSelect(option) {
      this.localConfig.macro_id = option?.id ?? null;
      this.emitUpdate();
    },
    normalizeAttachmentBlobIds(source = {}) {
      if (Array.isArray(source?.attachment_blob_ids)) {
        return source.attachment_blob_ids.filter(Boolean);
      }
      if (source?.attachment_blob_id) {
        return [source.attachment_blob_id];
      }
      return [];
    },
    onAttachmentInput(blobIds) {
      this.localConfig.attachment_blob_ids = Array.isArray(blobIds)
        ? blobIds.filter(Boolean)
        : [];
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('input', { ...this.localConfig });
    },
  },
};
</script>

<style scoped lang="scss">
.ai-email-config {
  @apply w-full flex flex-col gap-3;
}

.config-row {
  @apply flex w-full gap-3;
}

.config-row--primary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
}

.config-row--attachment {
  @apply flex-col;
}

.field-group {
  @apply flex flex-col min-w-0 w-full;
}

.field-group--full {
  @apply w-full min-w-0;
}

.action__question {
  @apply w-full max-w-full mb-0 mr-0;
}

.remove-action-btn {
  @apply shrink-0;
}

.ai-email-multiselect {
  @apply w-full min-w-0;

  :deep(.multiselect) {
    @apply mb-0 h-10 min-h-[2.5rem];
  }

  :deep(.multiselect__tags) {
    @apply h-10 min-h-[2.5rem] max-h-10 flex items-center overflow-hidden;
  }

  :deep(.multiselect__select) {
    @apply h-10 min-h-[2.5rem];
  }

  :deep(.multiselect__single),
  :deep(.multiselect__placeholder) {
    @apply block w-full max-w-full whitespace-nowrap overflow-hidden text-ellipsis mb-0 py-1 px-0.5;
  }

  .single-label-truncate,
  .option-truncate {
    @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;
  }

  :deep(.multiselect__content-wrapper) {
    @apply w-full;
    overflow-x: hidden !important;
  }

  :deep(.multiselect__content) {
    @apply w-full max-w-full block;
  }

  :deep(.multiselect__option) {
    @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;

    > span {
      @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;
    }
  }
}
</style>
